import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { organizationGroupOrganizationsAtom } from 'services/organizationGroupOrganizations/atom';
import { useUser } from 'core/stores/hooks/useUser';
import { useGetOrganizationsByOrganizationGroupIdQuery } from 'generated-graphql/hooks/apollo-hooks';
import { Organization } from 'generated-graphql/types';
import { extractQueryListItems } from 'core/utils/apollo/extractListItems';
import { TabV2Item } from 'common/ui/TabsV2';
import { VerticalTabItem } from 'common/ui/VerticalTabs';

export enum VisibilityFilter {
  Visible = 'visible',
  Hidden = 'hidden'
}

export const useAccountVisibility = () => {
  const user = useUser();
  const [organizationGroupOrganizations, setOrganizationGroupOrganizations] =
    useRecoilState(organizationGroupOrganizationsAtom);
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>(
    VisibilityFilter.Visible
  );
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    organizationGroupOrganizations?.[0]?.id ?? null
  );

  const {
    data: hiddenOrgsData,
    loading: hiddenOrgsLoading,
    refetch: refetchHiddenOrgs
  } = useGetOrganizationsByOrganizationGroupIdQuery({
    variables: {
      organizationGroupId: user.organizationGroupId as string,
      limit: 100,
      filter: { isHidden: { eq: true } }
    },
    skip: !user.organizationGroupId,
    fetchPolicy: 'cache-first'
  });

  const hiddenOrganizations = extractQueryListItems(
    hiddenOrgsData
  ) as Organization[];

  const filteredOrganizations =
    visibilityFilter === VisibilityFilter.Visible
      ? organizationGroupOrganizations ?? []
      : hiddenOrganizations;

  const selectedOrg = selectedOrgId
    ? filteredOrganizations.find((o) => o.id === selectedOrgId) ?? null
    : null;

  const verticalTabItems = filteredOrganizations.map((org) => ({
    id: org.id,
    title: org.name
  })) as VerticalTabItem[];

  const selectedIndex = selectedOrg
    ? Math.max(
        0,
        filteredOrganizations.findIndex((org) => org.id === selectedOrg.id)
      )
    : 0;

  const visibilityTabs: TabV2Item[] = [
    {
      label: 'Visible accounts',
      value: VisibilityFilter.Visible,
      count: organizationGroupOrganizations?.length ?? 0
    },
    {
      label: 'Hidden accounts',
      value: VisibilityFilter.Hidden,
      count: hiddenOrganizations.length,
      isCountLoading: hiddenOrgsLoading
    }
  ];

  const handleVisibilityFilterChange = (newFilter: string) => {
    const filter = newFilter as VisibilityFilter;
    setVisibilityFilter(filter);

    const newFilteredOrgs =
      filter === VisibilityFilter.Visible
        ? organizationGroupOrganizations ?? []
        : hiddenOrganizations;

    if (newFilteredOrgs.length > 0) {
      setSelectedOrgId(newFilteredOrgs[0].id);
    } else {
      setSelectedOrgId(null);
    }
  };

  // Awaited by AccountVisibilitySection so the loading overlay stays
  // visible until the refetch completes and the lists are up to date.
  const handleVisibilityChange = async (
    changedOrg: Organization,
    action: 'hide' | 'unhide'
  ) => {
    const remaining = filteredOrganizations.filter(
      (o) => o.id !== changedOrg.id
    );

    if (action === 'hide') {
      setOrganizationGroupOrganizations((prev) =>
        prev ? prev.filter((o) => o.id !== changedOrg.id) : prev
      );
    } else {
      setOrganizationGroupOrganizations((prev) => {
        if (!prev) return [changedOrg];
        if (prev.some((o) => o.id === changedOrg.id)) return prev;
        return [...prev, changedOrg];
      });
    }

    if (remaining.length > 0) {
      setSelectedOrgId(remaining[0].id);
    } else {
      setSelectedOrgId(null);
    }

    await refetchHiddenOrgs();
  };

  const handleOrgTabChange = (item: VerticalTabItem) => {
    setSelectedOrgId(item.id);
  };

  // When no visible accounts remain, auto-switch to the hidden tab so the
  // admin can immediately see and unhide accounts.
  useEffect(() => {
    if (
      visibilityFilter === VisibilityFilter.Visible &&
      organizationGroupOrganizations !== null &&
      organizationGroupOrganizations.length === 0 &&
      hiddenOrganizations.length > 0
    ) {
      handleVisibilityFilterChange(VisibilityFilter.Hidden);
    }
  }, [organizationGroupOrganizations, hiddenOrganizations.length]);

  const isLoadingHiddenTab =
    hiddenOrgsLoading &&
    visibilityFilter === VisibilityFilter.Hidden &&
    filteredOrganizations.length === 0;

  return {
    selectedOrg,
    visibilityFilter,
    visibilityTabs,
    filteredOrganizations,
    verticalTabItems,
    selectedIndex,
    isLoadingHiddenTab,
    handleVisibilityFilterChange,
    handleVisibilityChange,
    handleOrgTabChange
  };
};
